import {pt2px} from '@/utils/converter'
import React, { useMemo } from 'react'
import Image from 'next/image'
import 'yet-another-react-lightbox/styles.css'
import {useRouter} from 'next/router'
import { useTranslation } from 'next-i18next'
import { LockIcon } from '../Icons'

const formatImageUrl = (url: string | undefined) => {
  if (!url) return '/placeholder.png';
  
  // If it's already a full URL, return it directly (for AI-generated images)
  if (url.startsWith('http')) {
    return url;
  }
  
  // For relative paths, check if it's from our uploads
  if (url.startsWith('uploads/')) {
    const cleanPath = url.replace(/^\/+/, '');
    const cdnUrl = process.env.NEXT_PUBLIC_CDN_URL?.replace(/\/+$/, '') || '';
    return `${cdnUrl}/${cleanPath}`;
  }
  
  // For other relative paths (PDF pages), add CDN URL
  const cleanPath = url.replace(/^\/+/, '');
  const cdnUrl = process.env.NEXT_PUBLIC_CDN_URL?.replace(/\/+$/, '') || '';
  return `${cdnUrl}/${cleanPath}`;
};

const useIsLargeDevice = (breakpoint: number) => {
  const [isLarge, setIsLarge] = React.useState(false);

    React.useEffect(() => {
      const checkSize = () => setIsLarge(window.innerWidth >= breakpoint);
      checkSize();
      window.addEventListener('resize', checkSize);
      return () => window.removeEventListener('resize', checkSize);
    }, [breakpoint]);

    return isLarge;
  };

interface Props {
  pages: any
  pages_predefined_texts: any
  pages_dedication_texts: any[]
  product: any
  inputDedication: any
  cover_pages_predefined_texts: any
  cover_pages: any
  setStep?: any
  setActiveOption?: any
  setDedicationModal?: any
  name?:any
  dedicationImage?:any
  imageSelected?:any
  bookView?: string
  isProductRtlInBook?: any
  isInIsrael?: boolean
  visiblePageCount?: number
  isAIStory?: boolean
  isAIGenerating?: boolean
  aiLoadingStep?: number
  dedicationImageMode?: any
  aiStoryImage?: any
  isEdit?: boolean
}

export default function AIBookMobile({
  pages,
  pages_predefined_texts,
  product,
  pages_dedication_texts,
  inputDedication,
  cover_pages,
  cover_pages_predefined_texts,
  setStep,
  setActiveOption,
  setDedicationModal,
  name,
  dedicationImage,
  imageSelected,
  bookView='single',
  isProductRtlInBook,
  isInIsrael,
  visiblePageCount = Infinity,
  isAIStory,
  isAIGenerating,
  aiLoadingStep=0,
  dedicationImageMode,
  aiStoryImage,
  isEdit = false,
}: Props) {
  const {t} = useTranslation('personalizeModal');
  
  const aiLoadingSteps = useMemo(() => [
    t('aiGeneratingStory'),
    t('aiMakingHero'),
    t('aiAddingCharacters'),
    t('aiFinalizingIllustrations')
  ], [t]);
  
  const isLarge = useIsLargeDevice(1024);
  const modified_pages: any = []
  const [imageWidth, setImageWidth] = React.useState(380)
  const [open, setOpen] = React.useState(true)
  // FIXED: Always use correct indices for covers
  const firstCoverPageIndex = 0; // Always index 0 for first cover
  const lastCoverPageIndex = cover_pages && cover_pages.length > 1 ? 1 : 0; // Index 1 for last cover
  const [allImagesLoaded, setAllImagesLoaded] = React.useState(false);
  const shouldFlipPages = !isInIsrael;
  const {locale} = useRouter();
  const [currentStepIndex, setCurrentStepIndex] = React.useState(0);

  const router = useRouter()
  const isNotebookRoute = router.pathname.includes('notebook')
  
  const handleImageLoad = (event: any) => {
    const width = event.target.clientWidth
    if (width > 0) {
      setImageWidth(width)
    }
  }
  
  const slides = [
    {
      type: 'custom-slide',
    },
  ]
  const [page, setPage] = React.useState(0)
  const [scrollPosition, setScrollPosition] = React.useState(0)

  // Calculate which dedication page to show based on mode
  const getDedicationPageIndex = () => {
    // If no dedication image mode or 'no-image', show page without image (index 2)
    // If 'ai-image' or 'separate-image', show page with image (index 1)
    if (!dedicationImageMode || dedicationImageMode === 'no-image') {
      return 2; // Page without image placeholder
    } else {
      return 1; // Page with image placeholder
    }
  };

  const shouldShowDedicationImage = () => {
    if (!dedicationImageMode || dedicationImageMode === 'no-image') {
      return false;
    }
    
    if (dedicationImageMode === 'ai-image' && aiStoryImage) {
      return true;
    }
    
    if (dedicationImageMode === 'separate-image' && dedicationImage) {
      return true;
    }
    
    return false;
  };

  const getDedicationImageToShow = () => {
    if (dedicationImageMode === 'ai-image') {
      return aiStoryImage;
    } else if (dedicationImageMode === 'separate-image') {
      return dedicationImage;
    }
    return null;
  };

  const mobile_book_width =
    (document.getElementById('page-0')?.getBoundingClientRect().width ?? 0) + 25

  const shouldBlurPage = (pageIndex: number): boolean => {
    if (pageIndex === 0 || pageIndex === modified_pages.length + 1) {
      return false;
    }
    
    if (isAIStory && visiblePageCount < Infinity) {
      return pageIndex > visiblePageCount;
    }
    
    return false;
  };

  React.useEffect(() => {
    if (!isAIStory || !isAIGenerating) return;
    
    const interval = setInterval(() => {
      setCurrentStepIndex(prev => (prev + 1) % aiLoadingSteps.length);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [isAIStory, isAIGenerating, aiLoadingSteps]);

  const renderAIGeneratingOverlay = (index: number) => {
    if (!isAIStory || !isAIGenerating) return null;
    
    if (index > visiblePageCount) return null;
    
    return (
      <div className="absolute inset-0 z-[100] flex items-center justify-center bg-white/80 backdrop-blur-sm" dir={locale === 'ar' ? 'rtl': 'ltr'}>
        <div className="p-4 max-w-xs w-full mx-4">
          <div className="text-center">
            <h2 className="text-xl font-bold text-primary mb-3">
              {t('aiStoryGenerating')}
            </h2>
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-primary text-md leading-6 min-h-[3rem] line-clamp-2 animate-popup" key={aiLoadingSteps[currentStepIndex]}>
              {aiLoadingSteps[currentStepIndex]}
            </p>
          </div>
        </div>
      </div>
    );
  };
  
  const renderAIStoryLockOverlay = (index: number) => {
    const shouldBeBlurred = shouldBlurPage(index);
    if (!shouldBeBlurred) return null;
    
    return (
      <div className="absolute inset-0 z-50 flex flex-col items-center justify-center backdrop-blur-xl bg-white/40 rounded-md" dir={locale === 'ar' ? 'rtl': 'ltr'}>
        <div className="p-6 max-w-[80%] flex justify-center items-center">
          <div className="flex flex-col items-center justify-center gap-3" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
            <LockIcon/>
            <p className={`text-primary text-center font-medium lg:text-2xl mt-4`}>
              {t('premiumBuyText')}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Preload images
  React.useEffect(() => {
    if (isAIStory) {
      setAllImagesLoaded(true);
      return;
    }
    
    const preloadImages = async () => {
      try {
        const imageUrls = [];
        
        imageUrls.push(formatImageUrl(cover_pages[firstCoverPageIndex]));
        imageUrls.push(formatImageUrl(cover_pages[lastCoverPageIndex]));
        
        if (pages) {
          let pagesToUse = [...pages];
          if (shouldFlipPages) {
            for (let index = pagesToUse.length - 1; index >= 0; index--) {
              if ((index + 1) % 2 === 0) {
                continue;
              }
              modified_pages.push(pagesToUse[index + 1], pagesToUse[index]);
              slides.push(
                { type: 'custom-slide', ...pagesToUse[index + 1] },
                { type: 'custom-slide', ...pagesToUse[index] }
              );
            }
          } else {
            for (let index = 0; index < pages.length; index++) {
              if ((index + 1) % 2 === 0) {
                continue;
              }
              modified_pages.push(pages[index], pages[index + 1]);
              slides.push(
                { type: 'custom-slide', ...pages[index] },
                { type: 'custom-slide', ...pages[index + 1] }
              );
            }
          }
          for (let index = pages.length - 1; index >= 0; index--) {
            if ((index + 1) % 2 === 0) continue;
            if (pages[index + 1]) imageUrls.push(formatImageUrl(pages[index + 1]));
            if (pages[index]) imageUrls.push(formatImageUrl(pages[index]));
          }
        }
        
        // Preload dedication image based on mode
        if (shouldShowDedicationImage()) {
          const dedicationImageToShow = getDedicationImageToShow();
          if (dedicationImageToShow) {
            imageUrls.push(dedicationImageToShow);
          }
        }
        
        await Promise.all(
          imageUrls.map(url => {
            return new Promise((resolve, reject) => {
              const img = new (window as any).Image();
              img.src = url;
              img.onload = resolve;
              img.onerror = reject;
            });
          })
        );
        
        setAllImagesLoaded(true);
      } catch (error) {
        console.error('Error preloading images:', error);
        setAllImagesLoaded(true);
      }
    };
    
    preloadImages();
  }, [pages, cover_pages, dedicationImage, aiStoryImage, dedicationImageMode, firstCoverPageIndex, lastCoverPageIndex, isAIStory]);

  React.useEffect(() => {
    if (!allImagesLoaded) return;

    const handleScroll = () => {
      const mobileBookDiv = document.getElementById('mobile-book')
      if (mobileBookDiv) {
        let new_page: number = Math.floor(
          mobileBookDiv.scrollLeft / mobile_book_width,
        )
        if (new_page != 0) {
          new_page *= -1
        }
        setPage(new_page)
        setScrollPosition(mobileBookDiv.scrollLeft)
      }
    }

    const mobileBookDiv = document.getElementById('mobile-book')
    if (mobileBookDiv) {
      mobileBookDiv.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (mobileBookDiv) {
        mobileBookDiv.removeEventListener('scroll', handleScroll)
      }
    }
  }, [mobile_book_width, allImagesLoaded])

  if (!isAIStory && !allImagesLoaded) {
    return (
      <div className='flex-1 overflow-y-auto px-4 py-6 bg-[#FFFCEB] flex items-center justify-center gap-4 min-h-[75vh]' dir={locale === 'ar' ? 'rtl': 'ltr'}>
        <div className="w-6 h-6 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        <h2 className="text-lg font-semibold leading-none">{t('bookLoadingText')}</h2>
      </div>
    );
  }

  if (pages) {
    for (let index = pages.length - 1; index >= 0; index--) {
      if ((index + 1) % 2 === 0) {
        continue
      }
      modified_pages.push(pages[index + 1], pages[index])
      slides.push(
        {type: 'custom-slide', ...pages[index + 1]},
        {type: 'custom-slide', ...pages[index]},
      )
    }

    const getTextAlignment = (text_align: string) => {
      if (text_align == 'C') {
        return 'center'
      }
      if (text_align == 'R') {
        return 'right'
      }
      if (text_align == 'L') {
        return 'left'
      }
      return 'left'
    }

    const getTextPageNumber = (currentIndex: number): string => {
      // In edit mode, we've already reversed everything in transformBookData
      // So we can use direct index
      if (isEdit) {
        return currentIndex.toString();
      }
      
      // Original logic for non-edit mode
      if (!shouldFlipPages) {
        return currentIndex.toString();
      }
      
      const totalPages = pages.length;
      const pageMap: {[key: number]: string} = {};
      
      let originalPageCounter = totalPages;
      
      for (let i = 0; i < modified_pages.length; i += 2) {
        pageMap[i + 1] = (originalPageCounter - 1).toString();
        pageMap[i + 2] = (originalPageCounter - 2).toString();
        originalPageCounter -= 2;
      }
      return pageMap[currentIndex] || currentIndex.toString();
    };
    
    const getDimensions = (textDataArray: any[], pageNum: string) => {
      if (!textDataArray) return { width: 100, height: 100 };
      
      const pageData = textDataArray?.find((x: any) => x.page == pageNum);
      if (!pageData || !pageData.dimensions) {
        return { width: 100, height: 100 };
      }
      try {
        return JSON.parse(pageData.dimensions);
      } catch (e) {
        return { width: 100, height: 100 };
      }
    };

    const shouldRenderPage = (index: number): boolean => {
      if (!isAIStory) return true;
      return index <= visiblePageCount;
    };
    
    return (
      <div className='w-full'>
        <div className="mobile-book" id="mobile-book" key={`book-mobile-${bookView}-${dedicationImageMode}`}>
          <div className="relative w-full max-w-[360px] max-h-[360px] sm:max-w-[500px] sm:min-h-[500px] start-cover" id="page-0">
            {!isAIGenerating && cover_pages_predefined_texts
              ?.filter((x: any) => x.page == '0')[0]
              ?.predefined_texts?.map((item: any, idx: number) => {
                let dimensions = JSON.parse(
                  cover_pages_predefined_texts.filter(
                    (x: any) => x.page == '0',
                  )[0]['dimensions'],
                )
                return (
                  <div
                    className={`texts absolute ${isNotebookRoute ? 'z-[10]' : 'z-[120]'}`}
                    key={`mobile-text-${idx}-${bookView}`}
                    id={item.X_coord_percent}
                    style={{
                      fontSize:
                        (imageWidth * item.font_size) /
                          pt2px(dimensions.width) <
                        10
                          ? 8
                          : (imageWidth * item.font_size) /
                            pt2px(dimensions.width),
                      textAlign: getTextAlignment(item.text_align),
                      width:
                        (imageWidth * item.max_width) / pt2px(dimensions.width),
                      top: item.Y_coord_percent + '%',
                      lineHeight: item.line_height,
                      whiteSpace: 'pre-wrap',
                      left: item.X_coord_percent * 2 + '%',
                      color: item.color,
                      fontFamily: item.font_face + ',sans-serif',
                    }}
                    dangerouslySetInnerHTML={{__html: item.text}}></div>
                )
              })}
            <div className={`w-[380px] max-w-[390px] h-[380px] max-h-[390px] lg:w-[500px] lg:h-[500px] ${isNotebookRoute ? 'z-[5]' : 'z-[110]'}`}>
              <Image
                alt="cover"
                className="object-cover border-[1.5px] border-primary rounded-md p-1"
                fill
                priority={!isAIStory}
                onLoad={handleImageLoad}
                src={formatImageUrl(cover_pages[firstCoverPageIndex])}
                key={'cover page'}
                unoptimized={isAIStory ? true : false}
              />
            </div>
            {renderAIGeneratingOverlay(0)}
          </div>
          
          {(shouldFlipPages ? modified_pages : modified_pages.reverse())?.map((page: any, index: number) => {
            if (index == 0) {
              return <></>
            }
            
            const shouldRenderThisPage = shouldRenderPage(index);
            const textPageNumber = getTextPageNumber(index);
            
            if(index == 1 || index == 2) {
              // Only show the correct dedication page based on mode
              const dedicationPageToShow = getDedicationPageIndex();
              if (index !== dedicationPageToShow) return null;

              const dedicationPageData = pages_dedication_texts?.find((x: any) => x.page === textPageNumber);
              const predefinedPageData = pages_predefined_texts?.find((x: any) => x.page == textPageNumber);

              return (
                <div className={`relative w-full max-w-[360px] max-h-[360px] sm:max-w-[500px] sm:min-h-[500px]`} id={`page-mobile-${index}`} key={index}>
                  <div className='w-[380px] max-w-[390px] h-[380px] max-h-[390px] lg:w-[500px] lg:h-[500px]'>
                    <Image
                      alt="dedication page"
                      className={`block w-full h-full mob-book-image img-${index} border-[1.5px] border-primary rounded-md p-1 ${!shouldRenderThisPage ? 'filter blur-sm' : ''}`}
                      fill
                      priority={!isAIStory}
                      onLoad={handleImageLoad}
                      key={`dedication-bg-${index}-${dedicationImageMode}`}
                      src={formatImageUrl(page)}
                      unoptimized={isAIStory ? true : false}
                    />
                  </div>
                  
                  {renderAIGeneratingOverlay(index)}
                  {renderAIStoryLockOverlay(index)}
                  
                  {/* RENDER DEDICATION TEXTS */}
                  {dedicationPageData?.dedication_texts?.map((item: any, idx: number)=> {
                    let dimensions = getDimensions(pages_dedication_texts, textPageNumber);

                    if (item) {
                      const isImageBlock = !item.text;

                      const top = item.Y_coord_percent > 0
                        ? `${item.Y_coord_percent}%`
                        : `${(item.Y_coord / dimensions.height) * 71}%`;
                      const left = item.X_coord_percent > 0
                        ? `${item.X_coord_percent}%`
                        : `${(item.X_coord / dimensions.height) * 85}%`;
                      const imageTop = isLarge ? `${(item.Y_coord / dimensions.height) * 77.3}%` : `${(item.Y_coord / dimensions.height) * 78}%`;
                      const imageLeft = isLarge ? `${(item.X_coord / dimensions.height) * 74.6}%` : `${(item.X_coord / dimensions.height) * 74.4}%`;

                      const width = (imageWidth * item.max_width) / pt2px(dimensions.width);

                      if (isImageBlock) {
                        if (!shouldShowDedicationImage()) return null;
                        
                        const imageToShow = getDedicationImageToShow();
                        if (!imageToShow) return null;
                        
                        return (
                          <div
                            key={`image-${idx}`}
                            className="texts absolute z-[20]"
                            style={{
                              top: isImageBlock ? imageTop : top,
                              left: isImageBlock ? imageLeft : left,
                              width: width,
                              height: width,
                            }}
                          >
                            <img
                              src={imageToShow}
                              alt="Dedication Image"
                              className="object-cover rounded-full w-full h-full"
                            />
                          </div>
                        );
                      }

                      const maxWidth =
                        item.max_width && item.max_width > 0
                        ? `${item.max_width}px` 
                        : `${item.width_percent}%`;
                      return (
                          <div
                          key={`mobile-text-${idx}-${bookView}`}
                          className="texts absolute z-[20]"
                          style={{
                            fontSize:
                              (imageWidth * item.font_size) /
                                pt2px(dimensions.width) <
                              10
                                ? 8
                                : (imageWidth * item.font_size) /
                                  pt2px(dimensions.width),
                            textAlign: getTextAlignment(item.text_align),
                            width:
                              (imageWidth * item.max_width) / pt2px(dimensions.width),
                            top: top,
                            lineHeight: item.line_height,
                            whiteSpace: 'pre-wrap',
                            left: left,
                            color: item.color,
                            fontFamily: item.font_face + ',sans-serif',
                            maxWidth: maxWidth
                          }}
                        >
                          { item.text === 'أدخل النص' ? inputDedication.replace(
                                  '{basmti}',
                                  name ?? locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en,
                                ) : item.text?.includes('{basmti}')
                            ? item.text.replace('{basmti}', name ?? locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en)
                            : item.text}
                        </div>
                      )
                    } else{
                      return null;
                    }
                  })}
                  
                  {/* RENDER PREDEFINED TEXTS */}
                  {predefinedPageData?.predefined_texts?.map((item: any, idx: number) => {
                    let dimensions = getDimensions(pages_predefined_texts, textPageNumber);
                    if (item) {
                      return (
                        <div
                          className="texts absolute"
                          key={`mobile-predefined-${idx}-${bookView}`}
                          style={{
                            fontSize:
                              (imageWidth * item.font_size) /
                              pt2px(dimensions.width),
                            width:
                              (imageWidth * item.max_width) /
                              pt2px(dimensions.width),
                            top: item.Y_coord_percent + '%',
                            textAlign: getTextAlignment(item.text_align),
                            lineHeight: item.line_height,
                            whiteSpace: 'pre-wrap',
                            color: item.color,
                            left: item.X_coord_percent + '%',
                            fontFamily: item.font_face + ',sans-serif',
                          }}
                          dangerouslySetInnerHTML={{__html: item.text}}></div>
                      )
                    } else {
                      return null;
                    }
                  })}
                  
                  {inputDedication === '' && !isAIStory && (
                    <>
                    <div className='absolute -top-6 left-1/2 -translate-x-1/2 bg-primary text-white text-center p-4 h-[52px] w-contain rounded-lg text-sm cursor-pointer lg:hidden' onClick={()=>{
                      setStep('edit');
                      setActiveOption('dedication');
                    }}>
                      <p>{t('addDedicationText')}</p>
                    </div>
                    <div className='hidden lg:block absolute top-10 left-1/2 -translate-x-1/2 bg-primary text-white text-center p-4 h-[52px] w-contain rounded-lg cursor-pointer text-sm' onClick={()=>{
                      setDedicationModal(true);
                    }}>
                      <p>{t('addDedicationText')}</p>
                    </div>
                    </>
                  )}
                </div>
              )
            } else if(index !== 1 && index !== 2) {
              const dedicationPageData = pages_dedication_texts?.find((x: any) => x.page == textPageNumber);
              const predefinedPageData = pages_predefined_texts?.find((x: any) => x.page == textPageNumber);
              
              return (
                <div className="relative w-full max-w-[360px] max-h-[360px] sm:max-w-[500px] sm:min-h-[500px]" id={`page-mobile-${index}`} key={index}>
                  <div className='w-[380px] max-w-[390px] h-[380px] max-h-[390px] lg:w-[500px] lg:h-[500px]'>
                    <Image
                      alt="cover"
                      className={`block w-full mob-book-image img-${index} border-[1.5px] border-primary rounded-md p-1 ${!shouldRenderThisPage ? 'filter blur-sm' : ''}`}
                      fill
                      priority={!isAIStory}
                      onLoad={handleImageLoad}
                      key={index}
                      src={formatImageUrl(page)}
                      unoptimized={isAIStory ? true : false}
                    />
                  </div>
                  
                  {renderAIGeneratingOverlay(index)}
                  {renderAIStoryLockOverlay(index)}
                  
                  {/* RENDER DEDICATION TEXTS FOR REGULAR PAGES */}
                  {dedicationPageData?.dedication_texts?.map((item: any, idx: number) =>{
                    let dimensions = getDimensions(pages_dedication_texts, textPageNumber);
                    if (item) {
                      return (
                            <div
                              className={`texts absolute ${(index === 1 && inputDedication == '') ? 'mt-10': ''}`}
                              key={`mobile-dedication-${idx}-${bookView}`}
                              style={{
                                fontSize:
                                  (imageWidth * item.font_size) /
                                    pt2px(dimensions.width) <
                                  10
                                    ? 8
                                    : (imageWidth * item.font_size) /
                                      pt2px(dimensions.width),
                                width:
                                  (imageWidth * item.max_width) /
                                  pt2px(dimensions.width),
                                top: item.Y_coord_percent + '%',
                                whiteSpace: 'pre-wrap',
                                textAlign: getTextAlignment(item.text_align),
                                lineHeight: item.line_height,
                                color: item.color,
                                left: item.X_coord_percent + '%',
                                fontFamily: item.font_face + ',sans-serif',
                                direction: /^[a-zA-Z0-9\s.,!?;:'"\-()]*$/.test(inputDedication) ? 'ltr' : undefined,
                              }}>
                                {inputDedication.replace(
                                  '{basmti}',
                                  name ?? locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en,
                                )}
                            </div>
                      )
                    } else{
                      return null;
                    }
                  })}
                  
                  {/* RENDER PREDEFINED TEXTS FOR REGULAR PAGES */}
                  {predefinedPageData?.predefined_texts?.map((item: any, idx: number) => {
                    let dimensions = getDimensions(pages_predefined_texts, textPageNumber);
                    if (item) {
                      return (
                        <div
                          className="texts absolute"
                          key={`mobile-predefined-${idx}-${bookView}`}
                          style={{
                            fontSize:
                              (imageWidth * item.font_size) /
                              pt2px(dimensions.width),
                            width:
                              (imageWidth * item.max_width) /
                              pt2px(dimensions.width),
                            top: item.Y_coord_percent + '%',
                            textAlign: getTextAlignment(item.text_align),
                            lineHeight: item.line_height,
                            whiteSpace: 'pre-wrap',
                            color: item.color,
                            left: item.X_coord_percent + '%',
                            fontFamily: item.font_face + ',sans-serif',
                          }}
                          dangerouslySetInnerHTML={{__html: item.text}}></div>
                      )
                    } else {
                      return null;
                    }
                  })}
                  
                  {(inputDedication === '' && index === 1 && !isAIStory) && (
                    <>
                    <button className='bg-primary-500 py-4 px-6 absolute -top-4 right-0 left-0 mx-auto rounded-lg max-w-[150px] flex lg:hidden items-center justify-center' onClick={()=>{
                      setStep('edit');
                      setActiveOption('dedication')
                    }}>
                      <p className='font-medium text-white text-base'>اكتب اهداء</p>
                    </button>
                    <button className='bg-primary-500 py-4 px-6 absolute -top-4 right-0 left-0 mx-auto rounded-lg max-w-[150px] hidden lg:flex items-center justify-center' onClick={()=>{
                      setDedicationModal(true);
                    }}>
                      <p className='font-medium text-white text-base'>اكتب اهداء</p>
                    </button>
                    </>
                  )}
                </div>
              )
            }
            
            return null;
          })}

          <div className="relative w-full max-w-[360px] max-h-[360px] sm:max-w-[500px] sm:min-h-[500px] mb-72 scroll-mb-96" id={`page-mobile-${modified_pages.length + 1}`}>
            {cover_pages_predefined_texts
              ?.filter((x: any) => x.page == '1')[0]
              ?.predefined_texts?.map((item: any, idx: number) => {
                let dimensions = JSON.parse(
                  cover_pages_predefined_texts.filter(
                    (x: any) => x.page == '1',
                  )[0]['dimensions'],
                )
                return (
                  <div
                    className="texts absolute"
                    key={`mobile-text-${idx}-${bookView}`}
                    id={item.X_coord_percent}
                    style={{
                      fontSize:
                        (imageWidth * item.font_size) /
                          pt2px(dimensions.width) <
                        10
                          ? 8
                          : (imageWidth * item.font_size) /
                            pt2px(dimensions.width),
                      textAlign: getTextAlignment(item.text_align),
                      width:
                        (imageWidth * item.max_width) / pt2px(dimensions.width),
                      top: item.Y_coord_percent + '%',
                      lineHeight: item.line_height,
                      left: item.X_coord_percent * 2 + '%',
                      whiteSpace: 'pre-wrap',
                      color: item.color,
                      fontFamily: item.font_face + ',sans-serif',
                    }}
                    dangerouslySetInnerHTML={{__html: item.text}}></div>
                )
              })}
            <div className=' w-[380px] max-w-[390px] h-[380px] max-h-[390px] lg:w-[500px] lg:h-[500px] '>
              <Image
              alt="cover"
              className="object-cover border-[1.5px] border-primary rounded-md p-1"
              fill
              priority={!isAIStory}
              onLoad={handleImageLoad}
              src={formatImageUrl(cover_pages[lastCoverPageIndex])}
              unoptimized
            />
            </div>
            {renderAIGeneratingOverlay(0)}
          </div>
        </div>
      </div>
    )
  }
  return <></>
}